import { useMemo, useState } from 'react';
import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import BatteryChargingFullRoundedIcon from '@mui/icons-material/BatteryChargingFullRounded';
import RouteRoundedIcon from '@mui/icons-material/RouteRounded';
import heroDesktop from './images/z9gt/hero-desktop.jpg';
import heroMobile from './images/z9gt/hero-mobile.jpg';
import colorGreen from './images/z9gt/color-green.png';
import colorBlack from './images/z9gt/color-black.png';
import colorWhite from './images/z9gt/color-white.png';
import colorBlue from './images/z9gt/color-blue.png';
import highlightFront from './images/z9gt/highlight-front.jpg';
import highlightHero from './images/z9gt/highlight-hero.jpg';
import highlightBack from './images/z9gt/highlight-back.jpg';
import highlightRear from './images/z9gt/highlight-rear.jpg';
import highlightSide from './images/z9gt/highlight-side.jpg';
import flashCharging from './images/z9gt/flash-charging.jpg';
import pricePoster from './images/z9gt/z9gt-harga.jpg';
import PageSeo, {
  breadcrumbJsonLd,
  keywordSets,
  productJsonLd,
} from './PageSeo';
import ModelPriceSummary from './ModelPriceSummary';
import { createWhatsAppUrl } from './siteConfig';
import { vehiclePrices } from './vehiclePrices';

const PRICE = vehiclePrices.z9gt[0];

const exteriorColors = [
  { label: 'Fjord Green', value: 'green', swatch: '#0f6b63', image: colorGreen },
  { label: 'Obsidian Black', value: 'black', swatch: '#101214', image: colorBlack },
  { label: 'Snow White', value: 'white', swatch: '#f3f4f2', image: colorWhite },
  { label: 'Porcelain Blue', value: 'blue', swatch: '#7f9db9', image: colorBlue },
];

const interiorOptions = [
  {
    label: 'Perg Beige with Dark Chocolate',
    swatch: 'linear-gradient(135deg, #e6d5bb 0%, #4a2f23 100%)',
  },
  {
    label: 'Full Black Suede with Yellow Stitching',
    swatch: 'linear-gradient(135deg, #2a2a2a 0%, #0c0c0c 70%, #d9b63a 100%)',
  },
  {
    label: 'Red Velvet with Dark Chocolate',
    swatch: 'linear-gradient(135deg, #8d1f2d 0%, #3b221c 100%)',
  },
];

const exteriorHighlights = [
  {
    image: highlightFront,
    title: 'Dual Line Front Light',
    desc: 'Lampu depan dua garis yang tajam menjadi identitas wajah Z9 GT, dipadukan logo Danza di tengah.',
  },
  {
    image: highlightSide,
    title: 'Shooting-Brake Silhouette',
    desc: 'Proporsi golden ratio: rasio roda terhadap sumbu di atas 3:1 dan garis atap memanjang khas shooting brake.',
  },
  {
    image: highlightHero,
    title: 'Distinctive Wheel Design',
    desc: 'Velg besar dengan desain khas dan kaliper berwarna, menegaskan karakter performa 1.156 PS.',
  },
  {
    image: highlightBack,
    title: 'Rear Dual Tail Wing',
    desc: 'Sayap belakang ganda yang menyatu dengan bodi untuk stabilitas aerodinamika di kecepatan tinggi.',
  },
  {
    image: highlightRear,
    title: 'Time Hourglass Taillight',
    desc: 'Lampu belakang bermotif jam pasir yang menyala penuh, selebar bodi, mudah dikenali di malam hari.',
  },
];

const cabinFeatures = [
  {
    title: '13,2" Passenger Screen 2.5K',
    desc: 'Layar penumpang depan resolusi tinggi untuk hiburan dan navigasi, terpisah dari layar pengemudi.',
  },
  {
    title: 'Cocoon Class Rear Comfort',
    desc: 'Dua kursi belakang independen dengan pengaturan elektrik, pemanas, dan ventilasi.',
  },
  {
    title: 'Devialet 20 Speaker',
    desc: 'Sistem audio Devialet 20 speaker dengan ambient light multi-warna.',
  },
  {
    title: 'Kulkas Kabin -6 sampai 50 °C',
    desc: 'Konsol tengah berfungsi sebagai mini-fridge untuk mendinginkan atau menghangatkan minuman.',
  },
  {
    title: 'Digital Rearview Mirror',
    desc: 'Kaca spion digital dengan bidang pandang hingga 3 kali spion konvensional.',
  },
  {
    title: 'Wireless Charging 50 W Belakang',
    desc: 'Pengisian nirkabel cepat di armrest belakang, ditambah Meteor Welcome Light dan Smart Projection Tailgate.',
  },
];

const specGroups = [
  {
    title: 'Dimensi',
    rows: [
      ['Panjang x Lebar x Tinggi', '5.180 x 1.990 x 1.490 mm'],
      ['Wheelbase', '3.125 mm'],
      ['Radius putar minimum', '4,62 m'],
      ['Bagasi depan (frunk)', '53 L'],
      ['Bagasi belakang (kursi tegak / dilipat)', '495 / 1.680 L'],
    ],
  },
  {
    title: 'Powertrain & Performa',
    rows: [
      ['Baterai', 'Blade Battery Generasi 2 (LFP) + CTB'],
      ['Kapasitas baterai', '122,49 kWh'],
      ['Pengisian AC maksimum', '11 kW'],
      ['Pengisian DC', 'BYD Flash Charging'],
      ['Konfigurasi motor', 'Three-Motor, AWD'],
      ['Tenaga sistem maksimum', '1.156 PS / 850 kW'],
      ['Torsi sistem maksimum', '1.210 Nm'],
      ['Akselerasi 0-100 km/jam', '2,7 detik'],
      ['Kecepatan maksimum', '240 km/jam'],
      ['Jarak tempuh (NEDC)', '701 km'],
    ],
  },
];

const heroStats = [
  { icon: BoltRoundedIcon, value: '1.156 PS', label: 'Three-Motor AWD' },
  { icon: SpeedRoundedIcon, value: '2,7 detik', label: '0-100 km/jam' },
  { icon: RouteRoundedIcon, value: '701 km', label: 'Jarak tempuh NEDC' },
  { icon: BatteryChargingFullRoundedIcon, value: '9 menit', label: 'Penuh dengan Flash Charging' },
];

function JelajahiZ9GT() {
  const isMobile = useMediaQuery('(max-width:768px)');
  const [selectedColor, setSelectedColor] = useState('green');
  const currentColor = useMemo(
    () =>
      exteriorColors.find((option) => option.value === selectedColor) ||
      exteriorColors[0],
    [selectedColor],
  );

  return (
    <>
      <PageSeo
        title='Danza Z9 GT BSD Serpong | Harga Rp2,25 Miliar, Spesifikasi, Booking'
        description='Jelajahi Danza Z9 GT di BYD BSD Serpong. Shooting brake listrik three-motor AWD 1.156 PS, 0-100 km/jam 2,7 detik, Blade Battery Gen 2, BYD Flash Charging. Harga Rp2.250.000.000 OTR Jakarta.'
        keywords={[
          ...keywordSets.localDealer,
          'Danza Z9 GT Indonesia',
          'harga Danza Z9 GT',
          'Danza Z9 GT BSD Serpong',
          'Denza Z9 GT',
          'shooting brake listrik',
          'BYD Flash Charging',
        ]}
        path='/jelajahi-z9gt'
        image={heroDesktop}
        jsonLd={[
          productJsonLd({
            name: 'Danza Z9 GT',
            description:
              'Shooting brake listrik premium Danza Z9 GT dengan three-motor AWD 1.156 PS, Blade Battery Generasi 2, dan BYD Flash Charging.',
            image: heroDesktop,
            path: '/jelajahi-z9gt',
            price: PRICE.priceValue,
          }),
          breadcrumbJsonLd([
            { name: 'Beranda', path: '/' },
            { name: 'Danza Z9 GT', path: '/jelajahi-z9gt' },
          ]),
        ]}
      />

      <Box component='main' sx={{ width: '100%', overflow: 'hidden' }}>
        <HeroSection isMobile={isMobile} />

        <ColorSection
          currentColor={currentColor}
          selectedColor={selectedColor}
          setSelectedColor={setSelectedColor}
        />

        <FlashChargingSection />

        <HighlightSection />

        <CabinSection />

        <SpecSection />

        <PriceSection />

        <CtaSection />
      </Box>
    </>
  );
}

function HeroSection({ isMobile }) {
  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: { xs: '94vh', md: '100vh' },
        display: 'flex',
        alignItems: 'flex-end',
        overflow: 'hidden',
        backgroundColor: '#0b0f14',
      }}
    >
      <Box
        component='img'
        src={isMobile ? heroMobile : heroDesktop}
        alt='Danza Z9 GT shooting brake listrik warna Fjord Green'
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: { xs: 'center 60%', md: 'center' },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.05) 40%, rgba(0,0,0,0.72) 100%)',
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: 1240,
          mx: 'auto',
          px: { xs: 2, md: 5 },
          pb: { xs: 5, md: 7 },
          color: '#fff',
        }}
      >
        <Typography
          sx={{
            display: 'inline-block',
            px: 1.4,
            py: 0.5,
            borderRadius: '999px',
            backgroundColor: 'rgba(255,255,255,0.14)',
            border: '1px solid rgba(255,255,255,0.28)',
            fontSize: '0.78rem',
            fontWeight: 800,
            letterSpacing: 0.8,
            textTransform: 'uppercase',
          }}
        >
          Baru · Technology Drives Elegance
        </Typography>
        <Typography
          component='h1'
          sx={{
            mt: 1.5,
            fontSize: { xs: '2.6rem', sm: '3.6rem', md: '5rem' },
            fontWeight: 900,
            lineHeight: 0.98,
          }}
        >
          Danza Z9 GT
        </Typography>
        <Typography
          sx={{
            mt: 1.5,
            maxWidth: 680,
            fontSize: { xs: '1rem', md: '1.2rem' },
            lineHeight: 1.7,
            color: 'rgba(255,255,255,0.9)',
          }}
        >
          Shooting brake listrik three-motor AWD dengan Blade Battery Generasi 2
          dan BYD Flash Charging. {PRICE.price} OTR Jakarta.
        </Typography>

        <Box
          sx={{
            mt: { xs: 3, md: 4 },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: 1,
            maxWidth: 900,
          }}
        >
          {heroStats.map(({ icon: Icon, value, label }) => (
            <Box
              key={label}
              sx={{
                border: '1px solid rgba(255,255,255,0.26)',
                backgroundColor: 'rgba(0,0,0,0.4)',
                backdropFilter: 'blur(6px)',
                px: 2,
                py: 1.5,
                borderRadius: '10px',
              }}
            >
              <Icon sx={{ fontSize: 22, color: '#8fd3c8' }} />
              <Typography sx={{ mt: 0.5, fontSize: '1.35rem', fontWeight: 800 }}>
                {value}
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', opacity: 0.82 }}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function ColorSection({ currentColor, selectedColor, setSelectedColor }) {
  return (
    <Box
      sx={{
        backgroundColor: '#f4f6f8',
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 9 },
      }}
    >
      <Box
        sx={{
          maxWidth: 1240,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 340px' },
          gap: { xs: 4, lg: 5 },
          alignItems: 'center',
        }}
      >
        <Box>
          <Eyebrow>Exterior Color</Eyebrow>
          <SectionTitle>Pilih warna Danza Z9 GT</SectionTitle>
          <Box
            sx={{
              mt: 2,
              borderRadius: '16px',
              backgroundColor: '#fff',
              border: '1px solid rgba(17,24,39,0.08)',
              overflow: 'hidden',
            }}
          >
            <Box
              component='img'
              src={currentColor.image}
              alt={`Danza Z9 GT warna ${currentColor.label}`}
              sx={{ width: '100%', height: 'auto', display: 'block' }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            borderRadius: '12px',
            border: '1px solid rgba(17,24,39,0.12)',
            backgroundColor: '#fff',
            p: { xs: 2, sm: 3 },
            boxShadow: '0 12px 26px rgba(16,24,40,0.08)',
          }}
        >
          <Eyebrow>Selected exterior</Eyebrow>
          <Typography sx={{ mt: 0.5, fontSize: '1.8rem', fontWeight: 900 }}>
            {currentColor.label}
          </Typography>
          <Typography sx={{ mt: 1, lineHeight: 1.6, color: '#4b5563' }}>
            Empat pilihan warna resmi Danza Indonesia. Ketersediaan warna
            mengikuti stok dealer.
          </Typography>
          <Box sx={{ mt: 3, display: 'grid', gap: 1 }}>
            {exteriorColors.map((option) => {
              const active = selectedColor === option.value;
              return (
                <Button
                  key={option.value}
                  type='button'
                  variant='outlined'
                  onClick={() => setSelectedColor(option.value)}
                  sx={{
                    justifyContent: 'flex-start',
                    gap: 1.5,
                    py: 1.1,
                    px: 1.3,
                    borderRadius: '8px',
                    textTransform: 'none',
                    color: '#111827',
                    borderColor: active ? '#2d6cdf' : 'rgba(17,24,39,0.16)',
                    backgroundColor: active ? '#edf4ff' : 'transparent',
                    '&:hover': { borderColor: '#2d6cdf', backgroundColor: '#f7f9fc' },
                  }}
                >
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: '50%',
                      background: option.swatch,
                      border: '1px solid rgba(17,24,39,0.24)',
                      boxShadow: active ? '0 0 0 3px rgba(45,108,223,0.22)' : '',
                    }}
                  />
                  <Typography sx={{ fontWeight: 800 }}>{option.label}</Typography>
                </Button>
              );
            })}
          </Box>

          <Typography
            sx={{
              mt: 3,
              fontSize: '0.78rem',
              fontWeight: 800,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              color: '#667085',
            }}
          >
            Interior
          </Typography>
          <Box sx={{ mt: 1, display: 'grid', gap: 0.8 }}>
            {interiorOptions.map((option) => (
              <Box
                key={option.label}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: option.swatch,
                    border: '1px solid rgba(17,24,39,0.2)',
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ fontSize: '0.92rem', color: '#374151' }}>
                  {option.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function FlashChargingSection() {
  return (
    <Box
      sx={{
        backgroundColor: '#061418',
        color: '#fff',
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 9 },
      }}
    >
      <Box
        sx={{
          maxWidth: 1240,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.1fr 0.9fr' },
          gap: { xs: 3, md: 5 },
          alignItems: 'center',
        }}
      >
        <Box
          component='img'
          src={flashCharging}
          alt='BYD Flash Charging: ready in 5, full in 9 minutes, 10 flash charger pertama di dealer Danza'
          loading='lazy'
          sx={{
            width: '100%',
            height: 'auto',
            borderRadius: '16px',
            display: 'block',
            boxShadow: '0 30px 60px rgba(0,0,0,0.45)',
          }}
        />
        <Box>
          <Eyebrow light>Blade Battery Gen 2.0 + BYD Flash Charging</Eyebrow>
          <Typography
            component='h2'
            sx={{
              mt: 1,
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 900,
              lineHeight: 1.05,
            }}
          >
            Siap jalan dalam 5 menit, penuh dalam 9 menit
          </Typography>
          <Typography
            sx={{ mt: 2, lineHeight: 1.75, color: 'rgba(255,255,255,0.82)' }}
          >
            Blade Battery Generasi 2 berkapasitas 122,49 kWh dengan struktur
            Cell-to-Body (CTB) mendukung BYD Flash Charging. Sepuluh BYD Flash
            Charger pertama di Indonesia tersedia di dealer Danza, sehingga
            pengisian daya Z9 GT secepat mengisi bensin.
          </Typography>
          <Box
            sx={{
              mt: 3,
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 1.5,
            }}
          >
            {[
              ['5 menit', 'siap jalan'],
              ['9 menit', 'terisi penuh'],
              ['10 lokasi', 'Flash Charger pertama'],
            ].map(([value, label]) => (
              <Box
                key={label}
                sx={{
                  p: 1.5,
                  borderRadius: '10px',
                  border: '1px solid rgba(143,211,200,0.3)',
                  backgroundColor: 'rgba(143,211,200,0.08)',
                }}
              >
                <Typography sx={{ fontSize: '1.3rem', fontWeight: 900, color: '#8fd3c8' }}>
                  {value}
                </Typography>
                <Typography sx={{ fontSize: '0.82rem', opacity: 0.8 }}>
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function HighlightSection() {
  return (
    <Box sx={{ backgroundColor: '#fff', px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 } }}>
      <Box sx={{ maxWidth: 1240, mx: 'auto' }}>
        <Eyebrow>Exterior Design</Eyebrow>
        <SectionTitle>The Fluidity of Silk</SectionTitle>
        <Typography sx={{ mt: 1.5, maxWidth: 760, color: '#4b5563', lineHeight: 1.7 }}>
          Terinspirasi aliran kain sutra: garis bodi mulus, permukaan terpahat,
          dan proporsi shooting brake yang tidak dimiliki sedan listrik lain di
          kelasnya.
        </Typography>
        <Box
          sx={{
            mt: 4,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
            gap: 2.5,
          }}
        >
          {exteriorHighlights.map((item) => (
            <Box
              key={item.title}
              sx={{
                borderRadius: '14px',
                overflow: 'hidden',
                border: '1px solid rgba(17,24,39,0.08)',
                backgroundColor: '#fff',
                boxShadow: '0 12px 26px rgba(16,24,40,0.06)',
              }}
            >
              <Box
                component='img'
                src={item.image}
                alt={`Danza Z9 GT ${item.title}`}
                loading='lazy'
                sx={{ width: '100%', height: 'auto', display: 'block' }}
              />
              <Box sx={{ p: 2.2 }}>
                <Typography sx={{ fontWeight: 900, fontSize: '1.1rem' }}>
                  {item.title}
                </Typography>
                <Typography sx={{ mt: 0.6, color: '#4b5563', lineHeight: 1.6 }}>
                  {item.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function CabinSection() {
  return (
    <Box sx={{ backgroundColor: '#111827', color: '#fff', px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 } }}>
      <Box sx={{ maxWidth: 1240, mx: 'auto' }}>
        <Eyebrow light>Interior</Eyebrow>
        <SectionTitle light>Kabin kelas flagship</SectionTitle>
        <Box
          sx={{
            mt: 4,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {cabinFeatures.map((item) => (
            <Box
              key={item.title}
              sx={{
                p: 2.5,
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.12)',
                backgroundColor: 'rgba(255,255,255,0.05)',
              }}
            >
              <Typography sx={{ fontWeight: 900, fontSize: '1.05rem' }}>
                {item.title}
              </Typography>
              <Typography sx={{ mt: 0.8, lineHeight: 1.6, color: 'rgba(255,255,255,0.78)' }}>
                {item.desc}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function SpecSection() {
  return (
    <Box sx={{ backgroundColor: '#f4f6f8', px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 } }}>
      <Box sx={{ maxWidth: 1240, mx: 'auto' }}>
        <Eyebrow>Spesifikasi</Eyebrow>
        <SectionTitle>Danza Z9 GT Standard (AWD)</SectionTitle>
        <Box
          sx={{
            mt: 3,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2.5,
          }}
        >
          {specGroups.map((group) => (
            <Box
              key={group.title}
              component='table'
              sx={{
                width: '100%',
                borderCollapse: 'collapse',
                backgroundColor: '#fff',
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid rgba(17,24,39,0.08)',
                '& caption': {
                  textAlign: 'left',
                  px: 2,
                  py: 1.5,
                  fontWeight: 900,
                  color: '#fff',
                  backgroundColor: '#1f2937',
                },
                '& td': {
                  px: 2,
                  py: 1.2,
                  borderTop: '1px solid rgba(17,24,39,0.08)',
                  fontSize: '0.95rem',
                  verticalAlign: 'top',
                },
                '& td:first-of-type': { color: '#4b5563', width: '52%' },
                '& td:last-of-type': { fontWeight: 700, color: '#111827' },
              }}
            >
              <caption>{group.title}</caption>
              <tbody>
                {group.rows.map(([label, value]) => (
                  <tr key={label}>
                    <td>{label}</td>
                    <td>{value}</td>
                  </tr>
                ))}
              </tbody>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function PriceSection() {
  return (
    <Box sx={{ backgroundColor: '#fff' }}>
      <Box
        sx={{
          maxWidth: 1180,
          mx: 'auto',
          px: { xs: 2, md: 5 },
          pt: { xs: 6, md: 8 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '0.8fr 1.2fr' },
          gap: { xs: 3, md: 5 },
          alignItems: 'center',
        }}
      >
        <Box
          component='img'
          src={pricePoster}
          alt='Harga Danza Z9 GT IDR 2.250.000.000 OTR Jakarta'
          loading='lazy'
          sx={{
            width: '100%',
            height: 'auto',
            borderRadius: '16px',
            display: 'block',
            boxShadow: '0 20px 44px rgba(16,24,40,0.16)',
          }}
        />
        <Box>
          <Eyebrow>Harga resmi</Eyebrow>
          <SectionTitle>{PRICE.price}</SectionTitle>
          <Typography sx={{ mt: 1, color: '#4b5563', lineHeight: 1.7 }}>
            OTR Jakarta untuk Danza Z9 GT Standard three-motor AWD. Hubungi
            Junius untuk booking unit, pilihan warna, dan simulasi pembiayaan.
          </Typography>
        </Box>
      </Box>
      <ModelPriceSummary model='Danza Z9 GT' priceKey='z9gt' />
    </Box>
  );
}

function CtaSection() {
  return (
    <Box sx={{ px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 }, backgroundColor: '#f4f6f8' }}>
      <Box sx={{ maxWidth: 980, mx: 'auto', textAlign: 'center' }}>
        <Typography
          component='h2'
          sx={{ fontSize: { xs: '2rem', md: '3rem' }, fontWeight: 900, color: '#111827' }}
        >
          Tertarik dengan Danza Z9 GT?
        </Typography>
        <Typography sx={{ mt: 2, mx: 'auto', maxWidth: 780, color: '#4b5563', lineHeight: 1.7 }}>
          Hubungi Junius di BYD BSD Serpong untuk booking Danza Z9 GT, cek
          ketersediaan warna, promo, dan jadwal test drive.
        </Typography>
        <Button
          href={createWhatsAppUrl(
            `Halo Junius, saya tertarik dengan Danza Z9 GT (${PRICE.price}). Mohon info booking, warna, dan promonya.`,
          )}
          target='_blank'
          rel='noopener noreferrer'
          variant='contained'
          sx={{
            mt: 3,
            px: 4,
            py: 1.25,
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 800,
            backgroundColor: '#1f5fd1',
            '&:hover': { backgroundColor: '#184ca8' },
          }}
        >
          Tanya Harga Danza Z9 GT
        </Button>
      </Box>
    </Box>
  );
}

function Eyebrow({ children, light = false }) {
  return (
    <Typography
      sx={{
        color: light ? 'rgba(255,255,255,0.72)' : '#4c5867',
        fontWeight: 800,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        fontSize: '0.82rem',
      }}
    >
      {children}
    </Typography>
  );
}

function SectionTitle({ children, light = false }) {
  return (
    <Typography
      component='h2'
      sx={{
        mt: 1,
        fontSize: { xs: '2rem', md: '3rem' },
        fontWeight: 900,
        color: light ? '#fff' : '#111827',
        lineHeight: 1.05,
      }}
    >
      {children}
    </Typography>
  );
}

export default JelajahiZ9GT;
